import React, { useState, useEffect } from 'react'; 
import { useNavigate } from 'react-router-dom'; 
import { useApi } from '../../../Context/ApiContext'; 
import AcceptInvitationStatus from './AcceptInvitationStatus'; 
import RejectInvitationStatus from './RejectInvitationStatus'; 
import { useTranslation } from 'react-i18next'; 
import styled from 'styled-components';

// Styled components for modern UI
const Container = styled.div`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
`;

const Heading = styled.h2`
  font-size: 1.5rem;
  color: #333;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }
`;

const Button = styled.button`
  padding: 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #0056b3;
  }
`;

const LoadingText = styled.p`
  font-size: 1rem;
  color: #007bff;
`;

const ErrorText = styled.p`
  color: red;
  font-weight: bold;
`;

const DetailsContainer = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const DetailItem = styled.p`
  margin: 8px 0;
  font-size: 1rem;
  display: flex;
  justify-content: space-between;
`;

const InvitationActions = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
`;

// Main component
const ResolveInvitationUrl = () => {
  const { t } = useTranslation(); 
  const { resolveInvitation } = useApi(); 
  const [url, setUrl] = useState(''); 
  const [invitationId, setInvitationId] = useState(null); 
  const [invitationDetails, setInvitationDetails] = useState(null); 
  const [error, setError] = useState(''); 
  const [loading, setLoading] = useState(false); 
  const navigate = useNavigate();

  const extractInvitationId = (url) => {
    const urlParams = new URL(url);
    return urlParams.searchParams.get('id');
  };

  const handleResolveInvitation = async () => {
    const idToUse = invitationId || extractInvitationId(url);
    if (!idToUse) {
      setError(t('error') + ': ' + t('invalidInvitationId'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await resolveInvitation(idToUse);
      if (response && typeof response === 'object' && !Array.isArray(response)) {
        setInvitationDetails(response);
      } else if (Array.isArray(response) && response[0]) {
        setInvitationDetails(response[0]);
      } else {
        setError(t('unexpectedResponse'));
      }
    } catch (err) {
      setError(t('error') + ': ' + (err.message || t('tryAgain')));
    } finally {
      setLoading(false);
    }
  };

  const handleAssessmentClick = (assessmentId) => {
    if (!invitationDetails?.invitation?.checkpoint_id) {
      setError(t('checkpointIdMissing'));
      return;
    }
    navigate(`/seller/dashboard/AssessmentDetails/${assessmentId}`, {
      state: { 
        assessmentId, 
        invitationId: invitationDetails.invitation_id,
        checkpointId: invitationDetails.invitation.checkpoint_id
      },
    });
  };

  return (
    <Container>
      <Heading>{t('putTheInvitationUrlHere')}</Heading>

      {!invitationId && (
        <InputContainer>
          <Input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('pasteInvitationUrl')}
          />
          <Button onClick={handleResolveInvitation}>{t('resolveInvitation')}</Button>
        </InputContainer>
      )}

      {loading && <LoadingText>{t('loading')}...</LoadingText>}
      {error && <ErrorText>{error}</ErrorText>}

      {!loading && invitationDetails ? (
        <DetailsContainer>
          <h3>{t('invitationDetails')}</h3>
          <DetailItem>
            <strong>{t('checkpointId')}:</strong> {invitationDetails.invitation.checkpoint_id}
          </DetailItem>
          <DetailItem>
            <strong>{t('assessmentId')}:</strong> {invitationDetails.assessment_id}
          </DetailItem>
          <DetailItem><strong>{t('invitationId')}:</strong> {invitationDetails.invitation_id}</DetailItem>
          <DetailItem><strong>{t('buyerId')}:</strong> {invitationDetails.buyer_id}</DetailItem>
          <DetailItem><strong>{t('assessmentType')}:</strong> {invitationDetails.assessment_type}</DetailItem>
          <DetailItem><strong>{t('createdAt')}:</strong> {invitationDetails.created_at}</DetailItem>
          <DetailItem><strong>{t('lastUpdated')}:</strong> {invitationDetails.last_updated}</DetailItem>
          <DetailItem><strong>{t('status')}:</strong> {invitationDetails.status}</DetailItem>
        </DetailsContainer>
      ) : (
        !loading && <p>{t('')}</p>
      )}
      <Button onClick={() => handleAssessmentClick(invitationDetails.assessment_id)}>
        {t('viewDetails')}
      </Button>
    </Container>
  );
};

export default ResolveInvitationUrl;
